import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Loader2, ArrowRight, CheckCircle2 } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import Footer from "@/components/Footer";

const F = ["name", "work_email", "company", "evaluation_domain", "languages", "project_scale", "timeline", "message"];

export default function Pilot() {
  const [form, setForm] = useState(Object.fromEntries(F.map((k) => [k, ""])));
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Please enter your name."); return; }
    if (!form.work_email.trim() || !form.work_email.includes("@")) { toast.error("Please enter a valid work email."); return; }
    if (!form.company.trim()) { toast.error("Please enter your company or lab name."); return; }
    setSaving(true);
    try { await api.post("/pilot", form); setDone(true); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not submit. Please try again."); }
    finally { setSaving(false); }
  };

  const field = (k, label, req, ph, type = "text") => (
    <div>
      <label className="block text-sm text-[#D4D0C6] mb-1.5">{label} {req ? <span className="text-[#C79447]">*</span> : <span className="text-[#71717A]">(optional)</span>}</label>
      <input data-testid={`pilot-${k}`} type={type} value={form[k]} onChange={(e) => set(k, e.target.value)} placeholder={ph}
        className="w-full bg-transparent border border-white/15 text-[#F6F3EC] placeholder:text-[#52525B] px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#C79447] transition-colors" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F6F3EC]">
      <SiteNav />
      <section className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-24 pb-16 sm:pt-32 sm:pb-20 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="eyebrow text-[#C79447] reveal">For AI labs</p>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.06] reveal reveal-1">Discuss a pilot.</h1>
            <p className="mt-6 text-lg text-[#A1A1AA] leading-relaxed reveal reveal-2">If you're building LLMs, multimodal systems, reasoning models or domain-specific AI products, we'd love to understand your evaluation needs.</p>
            <p className="mt-6 inline-flex items-center gap-2 text-sm text-[#D4D0C6]"><span className="h-1.5 w-1.5 rounded-full bg-[#C79447]" />Direct scoping with our founding team · Response within 24 hours</p>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <div data-testid="pilot-done" className="border border-[#C79447]/40 bg-[#C79447]/[0.06] p-8 sm:p-10">
                <CheckCircle2 className="h-7 w-7 text-[#C79447]" strokeWidth={1.5} />
                <h2 className="mt-4 font-display text-2xl font-medium tracking-tight">Thank you — we've received your request.</h2>
                <p className="mt-3 text-[#A1A1AA] leading-relaxed">Our founding team will review your evaluation needs and respond directly within 24 hours.</p>
              </div>
            ) : (
              <div className="border border-white/10 p-6 sm:p-10 space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {field("name", "Name", true, "Your name")}
                  {field("work_email", "Work email", true, "you@company.com", "email")}
                  {field("company", "Company / lab", true, "Organisation name")}
                  {field("evaluation_domain", "Evaluation domain", false, "e.g. legal reasoning, Bengali")}
                  {field("languages", "Languages / expertise", false, "e.g. Bengali, Hindi")}
                  {field("project_scale", "Project scale", false, "e.g. ~20 evaluators")}
                </div>
                {field("timeline", "Timeline", false, "e.g. next quarter")}
                <div>
                  <label className="block text-sm text-[#D4D0C6] mb-1.5">Project context <span className="text-[#71717A]">(optional)</span></label>
                  <textarea data-testid="pilot-message" rows={4} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Tell us about the models and evaluation you have in mind."
                    className="w-full bg-transparent border border-white/15 text-[#F6F3EC] placeholder:text-[#52525B] px-3.5 py-2.5 text-sm resize-none focus:outline-none focus:border-[#C79447] transition-colors" />
                </div>
                <button data-testid="pilot-submit" onClick={submit} disabled={saving} className="inline-flex items-center gap-2 bg-[#C79447] text-[#0A0A0A] px-7 py-3.5 text-sm font-medium hover:brightness-110 transition-all disabled:opacity-60">
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}{saving ? "Sending…" : "Submit pilot request"} {!saving && <ArrowRight className="h-4 w-4" />}
                </button>
                <p className="text-xs text-[#52525B]">We'll only use these details to scope your evaluation needs.</p>
              </div>
            )}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
